import { useState, useEffect } from 'preact/hooks';
import { getConfig, type LiveChatConfig } from '../config';
import { useChatStatus } from '../hooks/useChatStatus';
import { navigateToView } from '../navigation';
import { t } from '../i18n';

interface Props {
  config: LiveChatConfig;
}

function getChatLayout() {
  const wc = getConfig();
  const pos = wc.chatPosition ?? 'bottom-right';
  const size = wc.chatSize ?? { width: 360, height: 480 };
  const z = wc.chatZIndex ?? 20000000;
  const [v, h] = pos.split('-');
  const bottom = v === 'bottom' ? '1rem' : 'auto';
  const top = v === 'top' ? '1rem' : 'auto';
  const right = h === 'right' ? '1rem' : 'auto';
  const left = h === 'left' ? '1rem' : 'auto';
  return { bottom, top, right, left, width: size.width, height: size.height, zIndex: z };
}

/**
 * Native LiveChat floating bubble.
 * Shows online/offline state; offline_action determines behavior when operators unavailable.
 * Phase 2 will add actual messaging; this is a functional stub with full UX flow.
 */
export function ChatBubble({ config }: Props) {
  const [open, setOpen] = useState(false);
  const status = useChatStatus();
  const offlineAction = config.offline_action ?? 'message';
  const isMobile = typeof window !== 'undefined'
    && !!window.matchMedia
    && window.matchMedia('(max-width: 1023px)').matches;

  useEffect(() => {
    const handler = () => setOpen(true);
    document.addEventListener('sw:open-chat', handler);
    return () => document.removeEventListener('sw:open-chat', handler);
  }, []);

  // Floating chat bubble/panel should not occupy viewport on mobile.
  if (isMobile) return null;
  if (status === 'offline' && offlineAction === 'hide') return null;

  const isOnline = status === 'online';
  const dotColor = isOnline ? '#22c55e' : '#9ca3af';
  const statusText = status === 'unknown'
    ? ''
    : isOnline
      ? t('chat.online', 'Online')
      : t('chat.offline', 'Offline');

  const handleOfflineAction = () => {
    if (offlineAction === 'ticket') {
      setOpen(false);
      navigateToView('tickets');
    }
  };

  const layout = getChatLayout();
  const containerStyle = `position:fixed;bottom:${layout.bottom};top:${layout.top};right:${layout.right};left:${layout.left};z-index:${layout.zIndex};font-family:var(--sw-font, system-ui, sans-serif)`;
  const panelStyle = `width:${layout.width}px;height:${layout.height}px;background:#fff;border-radius:var(--sw-radius-modal,0.75rem);box-shadow:var(--sw-shadow-modal, 0 8px 32px rgba(0,0,0,.18));display:flex;flex-direction:column;overflow:hidden;margin-bottom:.5rem`;

  return (
    <div style={containerStyle}>
      {open && (
        <div style={panelStyle}>
          {/* Header */}
          <div
            style="padding:.75rem 1rem;background:var(--sw-primary,#0d6efd);color:#fff;display:flex;justify-content:space-between;align-items:center"
          >
            <div style="display:flex;align-items:center;gap:.5rem">
              <span
                style={`width:8px;height:8px;border-radius:50%;background:${dotColor};display:inline-block;flex-shrink:0`}
              />
              <strong>{t('contact.live_chat')}</strong>
              {statusText && (
                <span style="font-size:.75rem;opacity:.85">&middot; {statusText}</span>
              )}
            </div>
            <button
              onClick={() => setOpen(false)}
              style="background:none;border:none;color:#fff;font-size:1.25rem;cursor:pointer;padding:0;line-height:1"
            >
              &times;
            </button>
          </div>

          {/* Body */}
          <div style="flex:1;display:flex;align-items:center;justify-content:center;padding:2rem;text-align:center;color:#6c757d">
            {isOnline ? (
              <p>{t('chat.coming_soon', 'Live chat is being set up. Please use email or phone for now.')}</p>
            ) : (
              <div>
                <p style="margin-bottom:1rem">
                  {t('chat.offline_message', 'Our operators are currently offline.')}
                </p>
                {offlineAction === 'ticket' && (
                  <button
                    onClick={handleOfflineAction}
                    style="padding:.5rem 1rem;background:var(--sw-primary,#0d6efd);color:#fff;border:none;border-radius:var(--sw-radius,0.375rem);cursor:pointer"
                  >
                    {t('contact.open_ticket')}
                  </button>
                )}
                {offlineAction === 'message' && (
                  <p style="font-size:.875rem">
                    {t('chat.leave_message', 'Please leave a message and we\'ll get back to you.')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating bubble button */}
      <button
        onClick={() => setOpen(!open)}
        style="width:56px;height:56px;border-radius:50%;background:var(--sw-primary,#0d6efd);color:#fff;border:none;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,.15);display:flex;align-items:center;justify-content:center;font-size:1.5rem;margin-left:auto;position:relative"
        aria-label={t('contact.live_chat')}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M5 17l-1 4 4-1h8a7 7 0 1 0 0-14H8a7 7 0 0 0-3 11z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
          <circle cx="9" cy="12" r="1" fill="currentColor" />
          <circle cx="12" cy="12" r="1" fill="currentColor" />
          <circle cx="15" cy="12" r="1" fill="currentColor" />
        </svg>
        <span
          style={`position:absolute;top:2px;right:2px;width:12px;height:12px;border-radius:50%;background:${dotColor};border:2px solid var(--sw-primary,#0d6efd)`}
        />
      </button>
    </div>
  );
}
